// Elapsed time, anchored to the server.
//
// The design ticked a client counter, which drifts, resets on reload and lies
// when two devices watch the same pod. Instead we take the open productivity
// line's start from Odoo, add the duration already banked in closed lines, and
// correct for clock skew measured against the server.

import { useEffect, useState } from 'react';
import type { WoLive } from '@/api/types';
import { useStore } from '@/store';

/** Re-render on an interval, without owning any state. */
export function useNow(intervalMs = 1000, active = true): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs, active]);
  return now;
}

export function useElapsedSeconds(live: WoLive | undefined): number {
  const skew = useStore((s) => s.serverSkewMs);
  const running = !!live?.startedAt;
  const now = useNow(1000, running);
  if (!live) return 0;
  if (!live.startedAt) return live.priorDurationSec;
  const serverNow = now - skew;
  return live.priorDurationSec + Math.max(0, Math.round((serverNow - live.startedAt) / 1000));
}

/** H:MM:SS, the spec's timer format. */
export function fmtElapsed(total: number): string {
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = Math.floor(total % 60);
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * The spec's anti-gaming gate: finishing in under half the expected time asks
 * for an actual start time and a reason. Skipped when no estimate exists —
 * duration_expected is 0 on a lot of real work orders.
 */
export function isTooQuick(elapsedSec: number, estimateMinutes: number): boolean {
  if (!estimateMinutes || estimateMinutes <= 0) return false;
  return elapsedSec > 0 && elapsedSec < 0.5 * estimateMinutes * 60;
}
